import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";

import useApi from "../../hooks/useApi";
import { weekDays } from "../../store/GlobalVarriables";
import VideoModal from "../../Components/VideoModal/VideoModal";
import CustomSelect from "../../Components/CustomSelect/CustomSelect";
import Loader from "../../Components/Loader";

import { ReactComponent as SearchIcon } from "../../static/images/search_logo.svg";
import { ReactComponent as FilterIcon } from "../../static/images/filter_icon.svg";
import { ReactComponent as ResetFilterIcon } from "../../static/images/reset_filter_icon.svg";

import "./style.scss";

const Finances = () => {
  const { get, loading } = useApi();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const invoiceRef = useRef();
  const sortRef = useRef(null);

  const [invoices, setInvoices] = useState();
  const [totalInvoiceData, setTotalInvoiceData] = useState();
  const [inviceSortToggl, setInviceSortToggl] = useState("");
  const [invoiceFilterValues, setInvoiceFilterValues] = useState("");

  const fetchInvoices = async (identity) => {
    const ps10005000 = JSON.parse(localStorage.getItem("ps10005000"));
    const accountID = jwtDecode(ps10005000).buid;

    const baseUrl = `/BusinessAccount/${accountID}/Invoices?`;
    const filteredUrl = `/BusinessAccount/${accountID}/Invoices?creationDate=${invoiceFilterValues?.creationDate || ""}&dueDate=${invoiceFilterValues?.dueDate || ""}${invoiceFilterValues?.open ? "&statuses=Open" : ""}${invoiceFilterValues?.paid ? "&statuses=Paid" : ""}`;

    try {
      const data = await get(
        // `/BusinessAccount/${accountID}/Invoices?creationDate=${""}&dueDate=${invoiceFilterValues?.dueDate || ""}${invoiceFilterValues?.open ? "&statuses=Open" : ""}${invoiceFilterValues?.paid ? "&statuses=Paid" : ""}`,
        identity === "filtered" ? filteredUrl : baseUrl,
      );

      const extractedInvoiceData = data?.invoices.map((item) => {
        let creationTime = item.activites.statusUpdates.map(
          (activity) => activity.activityTime,
        );

        return {
          name: item.payerDetails.name,
          currency: item.currency,
          amount: item.amount,
          scheduleDescription: item.payerDetails.scheduleDescription,
          scheduleDays: item.payerDetails.scheduleDays
            .sort((a, b) => weekDays.indexOf(a) - weekDays.indexOf(b))
            .map((day) => t(day.slice(0, 2).toLowerCase())),
          overDue:
            new Date(item.dueDate).toLocaleDateString() <
            new Date().toLocaleDateString()
              ? true
              : false,
          status: item.status,
          dueDate: item.dueDate,
          externalId: item.externalId,
          id: item.id,
          sessions: item.invoiceDetails.events.sort(
            (a, b) => new Date(b) - new Date(a),
          ),
          eventsCount: item.invoiceDetails.eventsCount,
          creationTime: creationTime[0],
        };
      });

      extractedInvoiceData.sort((a, b) => {
        const dateCompare = a.dueDate.localeCompare(b.dueDate);
        if (dateCompare !== 0) return dateCompare;
        return a.name.localeCompare(b.name);
      });

      invoiceRef.current = extractedInvoiceData;

      const extractedTotalInvoicedata = {
        totalEarned: data.totalEarned,
        trackedSessions: data.trackedSessions,
        currency: data.invoices[0]?.currency,
      };

      if (identity === "filtered" && extractedInvoiceData.length === 0) {
        setInvoices("filtered");
      } else {
        setInvoices(extractedInvoiceData);
        setTotalInvoiceData(extractedTotalInvoicedata);
      }
    } catch (err) {
      console.log(err);
    }
  };
  const convertDate = (value) => {
    const date = new Date(value);

    const day = date.getDate();
    const month = date.getMonth() + 1;

    const monthName = t(`months.${month}`);

    const formattedDate = `${day} ${monthName}`;

    return formattedDate;
  };

  const handleSearch = (value) => {
    setInvoices(
      invoiceRef.current.filter(
        (item) =>
          item.scheduleDescription
            .toLowerCase()
            .includes(value.toLowerCase()) ||
          item.name.toLowerCase().includes(value.toLowerCase()),
      ),
    );
  };

  useEffect(() => {
    fetchInvoices();
  }, [i18n.language]);

  return (
    <div className="finance-container">
      <div className="invoice-header">
        <div className="total-earned">
          <h3>{t("total_earned")}</h3>
          <h1>
            {totalInvoiceData?.totalEarned
              ? totalInvoiceData.totalEarned + " " + totalInvoiceData.currency
              : 0}
          </h1>
        </div>
        <div className="tracked-sessions">
          <h3>{t("tracked_sessions")}</h3>
          <h1>
            {totalInvoiceData?.trackedSessions
              ? totalInvoiceData.trackedSessions
              : 0}
          </h1>
        </div>
      </div>

      {totalInvoiceData?.currency || invoices === "filtered" ? (
        <>
          <div
            className={`invoice-filter-modal ${inviceSortToggl}`}
            onClick={() => {
              setInviceSortToggl("");
              setInvoiceFilterValues("");
            }}
          >
            <div
              className="invoice-filter-modal-content"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <h3 className="filter-invocie-header">
                {t("filter_invoices_by")}
              </h3>
              <div className="invoice-status-filter">
                <h4>{t("status")}</h4>
                <div>
                  <span
                    onClick={() => {
                      setInvoiceFilterValues((prevVal) => {
                        const isPaidActive = prevVal?.paid === "Paid";
                        const newFilters = { ...prevVal };
                        if (isPaidActive) {
                          delete newFilters.paid;
                        } else {
                          newFilters.paid = "Paid";
                        }
                        return newFilters;
                      });
                    }}
                    style={{
                      ...(invoiceFilterValues?.paid
                        ? {
                            backgroundColor: "var(--positive_bg_color)",
                            color: "#fff",
                          }
                        : {}),
                    }}
                  >
                    {t("paid")}
                  </span>

                  <span
                    onClick={() => {
                      setInvoiceFilterValues((prevVal) => {
                        const isUnpaidActive = prevVal?.open === "Open";
                        const newFilters = { ...prevVal };
                        if (isUnpaidActive) {
                          delete newFilters.open;
                        } else {
                          newFilters.open = "Open";
                        }
                        return newFilters;
                      });
                    }}
                    style={{
                      ...(invoiceFilterValues?.open
                        ? {
                            backgroundColor: "var(--positive_bg_color)",
                            color: "#fff",
                          }
                        : {}),
                    }}
                  >
                    {t("unpaid")}
                  </span>
                </div>
              </div>
              <div className="invoice-creation-filter">
                <h4>{t("month")}</h4>
                <CustomSelect
                  options={[
                    {
                      label: t("month"),
                      value: "",
                    },
                    {
                      label: t("january"),
                      value: "01",
                    },
                    {
                      label: t("february"),
                      value: "02",
                    },
                    {
                      label: t("march"),
                      value: "03",
                    },
                    {
                      label: t("april"),
                      value: "04",
                    },
                    {
                      label: t("may"),
                      value: "05",
                    },
                    {
                      label: t("june"),
                      value: "06",
                    },
                    {
                      label: t("july"),
                      value: "07",
                    },
                    {
                      label: t("august"),
                      value: "08",
                    },
                    {
                      label: t("september"),
                      value: "09",
                    },
                    {
                      label: t("october"),
                      value: "10",
                    },
                    {
                      label: t("november"),
                      value: "11",
                    },
                    {
                      label: t("december"),
                      value: "12",
                    },
                  ]}
                  defaultValue={"Month"}
                  onSelect={(val) =>
                    val === ""
                      ? setInvoiceFilterValues((prevVal) => {
                          const isMonthActive = prevVal?.creationDate !== "";
                          const newFilters = { ...prevVal };
                          if (isMonthActive) {
                            delete newFilters.creationDate;
                          } else {
                            newFilters.creationDate = val;
                          }
                          return newFilters;
                        })
                      : setInvoiceFilterValues((preVal) => ({
                          ...preVal,
                          creationDate: `${new Date().getFullYear()}-${val}-01T00:00:00.000Z`,
                        }))
                  }
                />
              </div>

              <div className="invoice-duedate-filter">
                <h4>
                  {t("due_date")}{" "}
                  <span>({t("all_invoices_before_this_date")})</span>
                </h4>
                <input
                  type="date"
                  onChange={(e) => {
                    const date = new Date(e.target.value);
                    if (date && !isNaN(new Date(date).getTime())) {
                      setInvoiceFilterValues((preVal) => ({
                        ...preVal,
                        dueDate: new Date(date).toISOString(),
                      }));
                    } else {
                      setInvoiceFilterValues((prevVal) => {
                        const { dueDate, ...rest } = prevVal;
                        return rest;
                      });
                    }
                  }}
                />
              </div>
              <div className="invoice-filter-actions">
                <button
                  className="invocie-cancel-button"
                  onClick={() => {
                    setInviceSortToggl("");
                  }}
                >
                  {t("cancel")}
                </button>
                <button
                  className="invocie-filter-button"
                  onClick={() => {
                    setInviceSortToggl("");
                    fetchInvoices("filtered");
                  }}
                >
                  {t("filter")}
                </button>
              </div>
            </div>
          </div>

          <div className="how-invoicing-work">
            <h1>{t("invoices")}</h1>

            <VideoModal
              videoLink={"Cw7-qv-E4xk"}
              buttonText={t("how_invoicing_work")}
            />
          </div>

          <div className="invoice-filter-container">
            <div className="invoice-search">
              <SearchIcon />
              <input
                type="text"
                placeholder={t("search_customer_group")}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
              />
            </div>
            <div className="invoice-filter-wrapper" ref={sortRef}>
              <div
                onClick={() => {
                  if (Object.keys(invoiceFilterValues).length > 0) {
                    setInvoiceFilterValues("");
                    fetchInvoices();
                  } else {
                    setInviceSortToggl("active");
                  }
                }}
              >
                {Object.keys(invoiceFilterValues)?.length > 0 ? (
                  <ResetFilterIcon />
                ) : (
                  <FilterIcon />
                )}
              </div>
            </div>
          </div>

          {invoices.length && invoices !== "filtered" ? (
            <div className="invoice-container">
              <ul>
                <>
                  {invoices?.map((item) => {
                    return (
                      <li
                        key={item.externalId}
                        onClick={() => {
                          navigate(`/finances/${item.externalId}`, {
                            state: {
                              consumerName: item.name,
                              sessions: item.sessions,
                              eventsCount: item.eventsCount,
                              externalId: item.externalId,
                              amount: item.amount,
                              currency: item.currency,
                              dueDate: item.dueDate,
                              overDue: item.overDue,
                              invoiceId: item.id,
                              userRole: "Provider",
                            },
                          });
                        }}
                      >
                        <div className="payment-card">
                          <div className="payment-card__header">
                            <h2 className="payment-card__name">{item.name}</h2>
                            <span className="payment-card__amount">
                              {item.currency + " " + item.amount}
                            </span>
                          </div>

                          <div className="payment-card__details">
                            <span className="payment-card__plan">
                              {item.scheduleDescription +
                                " - " +
                                item.scheduleDays.map((day) => " " + day)}
                            </span>

                            {item.status === "Paid" ? (
                              <span className="payment-card__date">
                                {t("paid_on") + " " + convertDate(item.dueDate)}
                              </span>
                            ) : item.status === "Open" && !item.overDue ? (
                              <span className="payment-card__date">
                                {t("due_on") + " " + convertDate(item.dueDate)}
                              </span>
                            ) : (
                              <span
                                className="payment-card__date"
                                style={{ color: "#c11313" }}
                              >
                                {t("due_on") + " " + convertDate(item.dueDate)}
                              </span>
                            )}
                          </div>

                          <div className="payment-card__footer">
                            <span className="payment-card__id">
                              {"#" + item.externalId}
                            </span>

                            {item.status === "Paid" ? (
                              <span
                                className={`payment-card__status payment-card__status--${"paid"}`}
                              >
                                {t("paid")}
                              </span>
                            ) : null}
                          </div>
                          <div className="creation-date">
                            <span>{`${t("created_at")} ${convertDate(item.creationTime)}`}</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </>
              </ul>
            </div>
          ) : invoices === "filtered" ? (
            <h1 className="no-filtered">{t("no_filtered")}</h1>
          ) : (
            <h1 className="no-filtered">{t("no_filtered")}</h1>
          )}
        </>
      ) : (
        <div className="no-invoices">
          <h2>{t("No_invoices")}</h2>
          <p>{t("invoices_auto_gen")}</p>
          <p>{t("invocies_appear")}</p>
          <ul>
            <li>{t("invocies_appear_month")}</li>
            <li>{t("invocies_appear_sessions")}</li>
          </ul>
          <p className="check-back">{t("invoices_check_back")}</p>
        </div>
      )}

      {loading && <Loader />}
    </div>
  );
};

export default Finances;
